import { Component } from '@theme/component';

/** Shared lifecycle for theme-editor re-renders and responsive enhancements. */
class ReferenceComponent extends Component {
  connectedCallback() { super.connectedCallback(); this.mount(); }
  updatedCallback() { super.updatedCallback(); this.unmount(); this.mount(); }
  disconnectedCallback() { this.unmount(); super.disconnectedCallback(); }
  mount() { this.abort = new AbortController(); this.cleanup = []; this.labels = JSON.parse(this.querySelector('[data-mr-labels]')?.textContent || '{}'); }
  label(key, fallback) { return this.labels[key] || fallback; }
  unmount() { this.abort?.abort(); for (const clean of this.cleanup || []) clean(); }
  listen(target, event, callback, options = {}) {
    target?.addEventListener(event, callback, { ...options, signal: this.abort.signal });
  }
}

class ReferenceHeader extends ReferenceComponent {
  mount() {
    super.mount();
    const nav = this.querySelector('.site-nav');
    const menu = this.querySelector('.menu-trigger');
    const header = this.querySelector('.site-header');
    if (!nav || !menu || !header) return;
    const groups = [...nav.querySelectorAll('details.nav-group')];
    const desktop = matchMedia('(min-width:1121px)');
    const closePanels = (except) => groups.forEach(group => { if (group !== except) group.open = false; });
    const measure = () => document.documentElement.style.setProperty('--site-header-bottom', `${header.getBoundingClientRect().bottom}px`);
    const closeMenu = () => {
      nav.classList.remove('open'); menu.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-label', this.label('open_menu', 'Open menu'));
      document.body.classList.remove('mr-nav-mobile-open'); closePanels();
    };
    this.cleanup.push(closeMenu);
    for (const group of groups) {
      const summary = group.querySelector('summary');
      this.listen(group, 'toggle', () => { if (group.open) { closePanels(group); measure(); } });
      this.listen(group, 'pointerenter', e => { if (desktop.matches && e.pointerType !== 'touch') { closePanels(group); group.open = true; measure(); } });
      this.listen(summary, 'click', e => { if (desktop.matches && e.detail > 0) { e.preventDefault(); closePanels(group); group.open = true; measure(); } });
      this.listen(summary, 'keydown', e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); closePanels(group); group.open = true; group.querySelector('a')?.focus(); }
      });
    }
    this.listen(menu, 'click', () => {
      const opened = !nav.classList.contains('open'); closePanels();
      nav.classList.toggle('open', opened); document.body.classList.toggle('mr-nav-mobile-open', opened);
      menu.setAttribute('aria-expanded', String(opened)); menu.setAttribute('aria-label', opened ? this.label('close_menu', 'Close menu') : this.label('open_menu', 'Open menu'));
      measure(); if (opened) groups[0]?.querySelector('summary')?.focus();
    });
    this.listen(document, 'pointerdown', e => { if (!this.contains(e.target) || e.target.closest('.menu-scrim')) closeMenu(); });
    this.listen(nav, 'pointerleave', () => { if (desktop.matches && !nav.contains(document.activeElement)) closePanels(); });
    this.listen(this, 'focusout', e => { if (desktop.matches && e.relatedTarget && !this.contains(e.relatedTarget)) closePanels(); });
    this.listen(nav, 'click', e => { if (e.target.closest('a')) closeMenu(); });
    this.listen(document, 'keydown', e => {
      if (e.key === 'Escape') {
        const open = groups.find(group => group.open);
        if (open) { open.open = false; open.querySelector('summary')?.focus(); }
        else if (nav.classList.contains('open')) { closeMenu(); menu.focus(); }
      }
      if (e.key === 'Tab' && !desktop.matches && nav.classList.contains('open')) {
        const items = [...this.querySelectorAll('a,button,summary,select')].filter(el => el.getClientRects().length && el.tabIndex >= 0);
        const first = items[0], last = items.at(-1);
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    });
    this.listen(desktop, 'change', closeMenu);
    this.listen(window, 'resize', measure);
    this.listen(window, 'scroll', measure, { passive: true });
    measure();
    const viewport = this.querySelector('#brand-menu-viewport');
    const slider = this.querySelector('#brand-menu-slider');
    const playback = this.querySelector('#brand-menu-playback');
    if (viewport && slider) {
      slider.classList.add('is-manual');
      let paused = matchMedia('(prefers-reduced-motion:reduce)').matches;
      const step = direction => {
        const width = (viewport.querySelector('.brand-menu-card')?.offsetWidth || 170) + 14;
        const max = viewport.scrollWidth - viewport.clientWidth;
        const left = direction > 0 && viewport.scrollLeft >= max - 2 ? 0 : direction < 0 && viewport.scrollLeft <= 0 ? max : viewport.scrollLeft + direction * width;
        viewport.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
      };
      const update = () => { if (playback) { playback.textContent = paused ? this.label('play', 'Play') : this.label('pause', 'Pause'); playback.setAttribute('aria-pressed', String(paused)); playback.setAttribute('aria-label', paused ? this.label('play_brands', 'Play brand carousel') : this.label('pause_brands', 'Pause brand carousel')); } };
      for (const button of slider.querySelectorAll('[data-brand-step]')) this.listen(button, 'click', () => { paused = true; update(); step(Number(button.dataset.brandStep)); });
      this.listen(playback, 'click', () => { paused = !paused; update(); });
      const timer = setInterval(() => { if (!paused && !document.hidden && viewport.getClientRects().length && !viewport.matches(':hover') && !viewport.contains(document.activeElement)) step(1); }, 3000);
      this.cleanup.push(() => clearInterval(timer)); update();
    }
    for (const link of nav.querySelectorAll('a')) {
      const url = new URL(link.href);
      if (url.pathname === location.pathname && url.search === location.search) {
        link.setAttribute('aria-current', 'page'); link.closest('details')?.querySelector('summary')?.classList.add('is-current');
      }
    }
  }
}

class ReferenceHome extends ReferenceComponent {
  mount() {
    super.mount();
    const showcase = this.querySelector('.home-showcase');
    const slides = [...this.querySelectorAll('.showcase-slide')];
    const tabs = [...this.querySelectorAll('[data-showcase-slide]')];
    const toggle = this.querySelector('[data-showcase-toggle]');
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    let active = 0, paused = reduced.matches, elapsed = 0, timer;
    const announce = this.querySelector('[data-showcase-status]');
    const show = (index, manual = false) => {
      if (!slides.length) return;
      active = (index + slides.length) % slides.length; elapsed = 0;
      slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === active); slide.setAttribute('aria-hidden', String(i !== active)); slide.inert = i !== active; });
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === active)); tab.tabIndex = i === active ? 0 : -1; const progress = tab.querySelector('b'); if (progress) progress.style.transform = `scaleX(${i === active ? 1 : 0})`; });
      if (manual && announce) announce.textContent = `${tabs[active]?.textContent.trim() || ''}, ${active + 1} ${this.label('of', 'of')} ${slides.length}`;
    };
    const sync = () => {
      showcase?.setAttribute('data-showcase-running', String(!paused));
      if (toggle) { toggle.setAttribute('aria-pressed', String(paused)); toggle.setAttribute('aria-label', paused ? this.label('play_slideshow', 'Play slideshow') : this.label('pause_slideshow', 'Pause slideshow')); const label = toggle.querySelector('[data-showcase-toggle-label]'); if (label) label.textContent = paused ? this.label('play', 'Play') : this.label('pause', 'Pause'); const icon = toggle.querySelector('.showcase-play-icon'); if (icon) icon.textContent = paused ? '▶' : 'Ⅱ'; }
    };
    for (const [i, tab] of tabs.entries()) {
      this.listen(tab, 'click', () => show(i, true));
      this.listen(tab, 'keydown', e => { const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0; if (delta) { e.preventDefault(); show(active + delta, true); tabs[active]?.focus(); } if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); show(e.key === 'Home' ? 0 : slides.length - 1, true); tabs[active]?.focus(); } });
    }
    this.listen(this.querySelector('[data-showcase-prev]'), 'click', () => show(active - 1, true));
    this.listen(this.querySelector('[data-showcase-next]'), 'click', () => show(active + 1, true));
    this.listen(toggle, 'click', () => { paused = !paused; sync(); });
    this.listen(reduced, 'change', () => { paused = reduced.matches; sync(); });
    let touch;
    this.listen(showcase, 'touchstart', e => { touch = e.touches[0]; }, { passive: true });
    this.listen(showcase, 'touchend', e => { if (!touch || e.target.closest('a,button')) return; const end = e.changedTouches[0], dx = end.clientX - touch.clientX, dy = end.clientY - touch.clientY; if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) show(active + (dx < 0 ? 1 : -1), true); touch = null; }, { passive: true });
    timer = setInterval(() => {
      if (paused || document.hidden || showcase?.contains(document.activeElement) || showcase?.matches(':hover') || document.querySelector('.site-header details[open]')) return;
      elapsed += 100;
      const progress = tabs[active]?.querySelector('b'); if (progress) progress.style.transform = `scaleX(${elapsed / 6000})`;
      if (elapsed >= 6000) show(active + 1);
    }, 100);
    this.cleanup.push(() => clearInterval(timer)); show(0); sync();

    const photos = [...this.querySelectorAll('[data-store-photo]')];
    const choices = [...this.querySelectorAll('[data-store-select]')];
    for (const choice of choices) this.listen(choice, 'click', () => {
      for (const photo of photos) { const current = photo.dataset.storePhoto === choice.dataset.storeSelect; photo.classList.toggle('is-current', current); photo.setAttribute('aria-hidden', String(!current)); }
      choices.forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
      const caption = this.querySelector('[data-store-caption]'); if (caption) caption.textContent = choice.textContent;
    });
    const video = this.querySelector('[data-store-video]');
    const videoButton = this.querySelector('[data-store-video-toggle]');
    if (video && videoButton) {
      const label = videoButton.querySelector('[data-video-label]');
      const updateVideo = () => { const text = video.paused ? this.label('play_video', 'Play store tour') : this.label('pause_video', 'Pause store tour'); videoButton.setAttribute('aria-label', text); videoButton.setAttribute('aria-pressed', String(!video.paused)); if (label) label.textContent = text; const icon = videoButton.querySelector('[data-video-icon]'); if (icon) icon.textContent = video.paused ? '▶' : 'Ⅱ'; };
      this.listen(videoButton, 'click', async () => {
        if (!video.src) video.src = video.dataset.src;
        if (video.paused) { try { await video.play(); } catch { if (label) label.textContent = 'Try playing again'; } } else video.pause();
        updateVideo();
      });
      this.listen(video, 'ended', updateVideo); this.cleanup.push(() => video.pause()); updateVideo();
    }
    for (const button of this.querySelectorAll('[data-oem-pause], [data-store-motion]')) {
      this.listen(button, 'click', () => { const paused = button.getAttribute('aria-pressed') !== 'true'; button.setAttribute('aria-pressed', String(paused)); const region = button.closest('[data-oem-story], [data-store-scene]'); region?.classList.toggle('mr-motion-paused', paused); button.textContent = paused ? this.label('resume_motion', 'Resume motion') : this.label('pause_motion', 'Pause motion'); });
    }
  }
}

if (!customElements.get('mr-reference-header')) customElements.define('mr-reference-header', ReferenceHeader);
if (!customElements.get('mr-reference-home')) customElements.define('mr-reference-home', ReferenceHome);

class BikeDirectory extends ReferenceComponent {
  mount() {
    super.mount();
    const cards = [...this.querySelectorAll('[data-mr-card]')];
    const buttons = [...this.querySelectorAll('[data-mr-type]')];
    const brand = this.querySelector('[data-mr-brand]:is(select)');
    const search = this.querySelector('[data-mr-search]');
    let type = 'all';
    const apply = () => {
      const query = search.value.trim().toLocaleLowerCase();
      let count = 0;
      cards.forEach(card => {
        const show = (type === 'all' || card.dataset.mrEngine === type) && (!brand.value || card.dataset.mrBrand === brand.value) && (!query || card.dataset.mrTitle.toLocaleLowerCase().includes(query));
        card.hidden = !show; if (show) count++;
      });
      this.querySelectorAll('[data-mr-group]').forEach(group => { group.hidden = ![...group.querySelectorAll('[data-mr-card]')].some(card => !card.hidden); });
      this.querySelector('[data-mr-count]').textContent = String(count);
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mrType === type)));
    };
    buttons.forEach(button => this.listen(button, 'click', () => { type = button.dataset.mrType; apply(); }));
    this.listen(brand, 'change', apply); this.listen(search, 'input', apply); apply();
  }
}
if (!customElements.get('mr-bike-directory')) customElements.define('mr-bike-directory', BikeDirectory);
